import { Button } from "components/buttons/button";
import { StandardInput } from "components/forms/input";
import gql from "graphql-tag";
import {
  Roles_Enum,
  useGetEventCommentsQuery,
  useInsertCommentMutation,
  useToggleEventCommentsMutation,
} from "lib/graphql-generated";
import { useInput } from "lib/hooks/use-input";
import { useUser } from "lib/hooks/use-user";
import { FormEvent, MouseEvent, useEffect, useRef, useState } from "react";
import { ChatComment } from "components/events/chat/comment";
import { ChatQuestion } from "./question";

gql`
  query GetEventComments($eventId: uuid!) {
    event_comment(
      where: { event_id: { _eq: $eventId } }
      order_by: { created_at: asc }
    ) {
      id
      comment
      is_question
      public_account {
        id
        display_name
      }
    }
  }
`;

gql`
  mutation InsertComment($comment: String!, $eventId: uuid!) {
    insert_event_comment_one(
      object: { comment: $comment, event_id: $eventId }
    ) {
      id
      comment
      is_question
      public_account {
        id
        display_name
      }
    }
  }
`;

gql`
  mutation ToggleEventComments($eventId: uuid!, $commentsEnabled: Boolean) {
    update_events_by_pk(
      pk_columns: { id: $eventId }
      _set: { enable_comments: $commentsEnabled }
    ) {
      id
      enable_comments
    }
  }
`;

interface Props {
  eventId: string;
  enable: boolean;
}
export const Chat = ({ eventId, enable }: Props) => {
  const { userData } = useUser();
  const [tab, setTab] = useState<"comments" | "questions">("comments");
  const comment = useInput<string>("");
  const commentsRef = useRef<HTMLDivElement>(null);

  const { data, networkStatus } = useGetEventCommentsQuery({
    variables: {
      eventId: eventId,
    },
    pollInterval: 500,
  });

  useEffect(() => {
    if (commentsRef.current) {
      commentsRef.current.addEventListener("DOMNodeInserted", (event) => {
        const { currentTarget: target } = event;
        target["scroll"]({ top: target["scrollHeight"], behaviour: "smooth" });
      });
    }
  }, []);

  const [insertComment] = useInsertCommentMutation();
  const [toggle] = useToggleEventCommentsMutation({
    variables: {
      eventId: eventId,
      commentsEnabled: !enable,
    },
    refetchQueries: ["GetEvent"],
  });

  const handleInsertComment = (e: any) => {
    e.preventDefault();
    insertComment({
      variables: {
        comment: comment.value,
        eventId: eventId,
      },
    }).then(() => {
      comment.init("");
    });
  };

  const comments = data?.event_comment;
  const questions = data?.event_comment.filter((c) => c.is_question);

  return (
    <>
      <section className=' rounded-md p-4 flex flex-col '>
        {!enable ? (
          <section className='flex flex-col space-y-8'>
            <p className='prose mx-auto'>
              Commenting is not enabled for this event.{" "}
            </p>
            {userData?.role === Roles_Enum.Bookseller && (
              <Button
                buttonType='secondary'
                buttonSize='xl'
                type='button'
                onClick={() => toggle()}>
                Turn ON Comments
              </Button>
            )}
          </section>
        ) : (
          <>
            {userData?.role === Roles_Enum.Bookseller && (
              <Button
                className='mx-auto mb-8'
                buttonType='secondary'
                buttonSize='xl'
                type='button'
                onClick={() => toggle()}>
                Turn OFF Comments
              </Button>
            )}
            <ul className='flex flex-row justify-center space-x-8 mb-4'>
              <li className='flex -mb-px'>
                <button
                  type='button'
                  onClick={() => setTab("comments")}
                  className={`${
                    tab === "comments"
                      ? "text-white bg-yellow-dark border-green-default "
                      : "border-transparent text-gray-500 hover:text-green-default"
                  }  -mb-px whitespace-nowrap py-2 font-medium px-8 rounded-t-lg border-b-2`}>
                  Comments
                </button>
              </li>

              <li className='flex -mb-px'>
                <button
                  type='button'
                  onClick={() => setTab("questions")}
                  className={`${
                    tab === "questions"
                      ? "text-white bg-yellow-dark border-green-default "
                      : "border-transparent text-gray-500 hover:text-green-default"
                  }  -mb-px whitespace-nowrap py-2 font-medium px-8 rounded-t-lg border-b-2`}>
                  Questions
                </button>
              </li>
            </ul>
            {tab === "comments" ? (
              <>
                <article
                  className='border bg-white border-gray-200 rounded-lg flex flex-col skew-y-2 p-4 h-96 overflow-scroll'
                  ref={commentsRef}>
                  {comments?.map((c) => (
                    <ChatComment commentObject={c} />
                  ))}
                </article>
                <form
                  className='flex flex-col space-y-1'
                  onSubmit={handleInsertComment}>
                  <StandardInput
                    id='comment'
                    name='comment'
                    type='text'
                    label=''
                    inputProps={{
                      placeholder: !userData ? "Signin to comment" : "Comment",
                      disabled: !userData,
                    }}
                    bind={{ ...comment.bind }}
                  />
                  <Button
                    buttonType='secondary'
                    type='submit'
                    onClick={handleInsertComment}
                    disabled={
                      !userData || !userData || comment.value.trim() === ""
                    }>
                    Send
                  </Button>
                </form>
              </>
            ) : (
              <>
                <article className='border bg-white border-gray-200 rounded-lg flex flex-col skew-y-2 p-4 h-96 overflow-scroll'>
                  {questions?.map((q) => (
                    <ChatQuestion commentObject={q} />
                  ))}
                </article>
              </>
            )}
          </>
        )}
      </section>
    </>
  );
};
